// src/types.ts
var EventHeaders = /* @__PURE__ */ ((EventHeaders2) => {
  EventHeaders2["PriorEventId"] = "x-prior-event-uuid";
  return EventHeaders2;
})(EventHeaders || {});
var EventQueryParameters = /* @__PURE__ */ ((EventQueryParameters2) => {
  EventQueryParameters2["PriorEventId"] = "peid";
  return EventQueryParameters2;
})(EventQueryParameters || {});

// src/event-tracker/event-tracker.ts
import { v4 as uuidv4 } from "uuid";

// src/events/types.ts
var baseEvents = {
  viewItem: {
    event_name: "view_item",
    event_type: "view",
    event_subtype: "impression"
  },
  viewItemList: {
    event_name: "view_item_list",
    event_type: "view",
    event_subtype: "impression"
  },
  viewItemListViewed: {
    event_name: "view_item_list",
    event_type: "view",
    event_subtype: "impression_viewed"
  },
  viewItemListSearch: {
    event_name: "view_item_list",
    event_type: "view",
    event_subtype: "impression_search"
  },
  viewItemListSearchViewed: {
    event_name: "view_item_list",
    event_type: "view",
    event_subtype: "impression_search_viewed"
  },
  search: {
    event_name: "search",
    event_type: "action",
    event_subtype: "search"
  },
  searchRefinement: {
    event_name: "search",
    event_type: "action",
    event_subtype: "search_refinement"
  },
  inclick: {
    event_name: "select_item",
    event_type: "action",
    event_subtype: "inclick"
  },
  optInAction: {
    event_name: "interaction",
    event_type: "action",
    event_subtype: "opt-in"
  },
  optOutAction: {
    event_name: "interaction",
    event_type: "action",
    event_subtype: "opt-out"
  },
  vote: {
    event_name: "interaction",
    event_type: "action",
    event_subtype: "vote"
  },
  personalizationSignal: {
    event_name: "interaction",
    event_type: "action",
    event_subtype: "personalization_signal"
  }
};

// src/utilities.ts
var buildViewArea = (view, clientData) => {
  if (clientData.page.entityLayout) {
    view.entity_layout = {
      render: clientData.page.entityLayout
    };
  }
  if (clientData.page.contentLayout) {
    view.content_layout = {
      render: clientData.page.contentLayout
    };
  }
  return view;
};
function parseEventDataFromDataLayer({
  page,
  reporting,
  traffic,
  visitor
}) {
  return {
    footStepEventId: reporting.footStepEventId,
    clientUuid: reporting.clientID,
    anonymousUuid: reporting.auuid,
    sessionUuid: reporting.sessionID,
    attributionUuid: reporting.attributionUuid,
    userId: visitor.userId,
    entityLayout: page.entityLayout,
    contentLayout: page.contentLayout,
    experience: visitor.experience,
    deviceType: traffic.deviceSegment.length > 0 ? traffic.deviceSegment[0].toUpperCase() + traffic.deviceSegment.slice(1) : "Unknown"
  };
}
function parseEventDataFromUrl(url) {
  const urlObj = new URL(url, "https://event-tracker.slickdeals.net");
  return {
    priorEventId: urlObj.searchParams.get("peid" /* PriorEventId */)
  };
}
function determineItemType(uniqueId) {
  return /pco/i.test(uniqueId) ? "PCO" : "DEAL";
}
var getClickActionType = (clientData) => clientData.device.deviceType === "Mobile" ? "tap" /* Tap */ : "click" /* Click */;

// src/event-tracker/event-tracker.ts
var SEND_EVENT_ENDPOINT = "https://cwogzftn.usw.stape.io";
var SEND_IMPRESSION_EVENT_ENDPOINT = "https://l7ox5un97g.execute-api.us-west-2.amazonaws.com/sqs/send/data-solutions-events-impressions";
var EventTracker = class {
  clientData = {
    priorEventId: "",
    clientUuid: "",
    anonymousUuid: "",
    sessionUuid: "",
    attributionUuid: "",
    userId: 0,
    device: {
      deviceType: "",
      experience: "",
      userAgent: ""
    },
    page: {
      entityLayout: "",
      contentLayout: ""
    }
  };
  eventQueue = [];
  static getInstance() {
    return window.sdEventTracker ??= new this();
  }
  init({ client }) {
    this.initState(client);
    this.initEventQueue();
    this.scrubAndReplaceCurrentUrl(client.url);
    this.initClickParamInjector();
    return this;
  }
  getPriorEventId() {
    return this.clientData.priorEventId;
  }
  initState({
    dataLayer,
    url,
    device,
    whitelistedEvents
  }) {
    const {
      footStepEventId,
      clientUuid,
      anonymousUuid,
      sessionUuid,
      attributionUuid,
      userId,
      entityLayout,
      contentLayout,
      experience,
      deviceType
    } = parseEventDataFromDataLayer(dataLayer);
    const { priorEventId } = parseEventDataFromUrl(url);
    this.clientData.priorEventId = footStepEventId || priorEventId || "";
    this.clientData.clientUuid = clientUuid;
    this.clientData.anonymousUuid = anonymousUuid;
    this.clientData.sessionUuid = sessionUuid;
    this.clientData.attributionUuid = attributionUuid;
    this.clientData.userId = userId;
    this.clientData.device.deviceType = deviceType;
    this.clientData.device.experience = experience;
    this.clientData.device.userAgent = device.userAgent;
    this.clientData.page.entityLayout = entityLayout ?? "";
    this.clientData.page.contentLayout = contentLayout ?? "";
  }
  initEventQueue() {
    this.eventQueue.push = (...items) => {
      void this.processQueue(items);
      return 0;
    };
    void this.processQueue(this.eventQueue);
  }
  scrubAndReplaceCurrentUrl(url) {
    const newUrl = new URL(url);
    newUrl.searchParams.delete("peid" /* PriorEventId */);
    window.history.replaceState(void 0, "", newUrl);
  }
  injectPriorEventUuid(ev) {
    if (ev.target instanceof HTMLAnchorElement) {
      const targetEl = ev.target;
      if (targetEl.href && !targetEl.getAttribute("href")?.startsWith("javascript") && !targetEl.getAttribute("href")?.startsWith("#")) {
        const linkTarget = new URL(targetEl.href);
        if (linkTarget.hostname == location.hostname) {
          linkTarget.searchParams.set("peid", this.clientData.priorEventId);
          targetEl.href = linkTarget.toString();
        }
      }
    }
  }
  initClickParamInjector() {
    document.body.addEventListener(
      "mousedown",
      this.injectPriorEventUuid.bind(this)
    );
    document.body.addEventListener(
      "touchstart",
      this.injectPriorEventUuid.bind(this)
    );
  }
  processQueue(queue) {
    return this.sendEvents(
      queue.map((queuedEvent) => this.buildQueuedEvent(queuedEvent))
    );
  }
  trackEvent(event) {
    this.eventQueue.push({
      event,
      queuedAt: Date.now()
    });
  }
  async sendEvents(events) {
    const filteredEvents = this.filterOutNonWhitelistedEvents(events);
    if (filteredEvents.length === 0) {
      return;
    }
    const [impressions, nonImpressions] = this.splitImpressionEvents(filteredEvents);
    if (impressions.length > 0) {
      try {
        await fetch(SEND_IMPRESSION_EVENT_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-slickdeals-auth": "secretToken"
          },
          body: JSON.stringify(impressions)
        });
      } catch (error) {
        console.error(error);
      }
    }
    if (nonImpressions.length > 0) {
      try {
        await fetch(SEND_EVENT_ENDPOINT, {
          method: "POST",
          mode: "no-cors",
          body: JSON.stringify(nonImpressions)
        });
      } catch (error) {
        console.error(error);
      }
    }
  }
  filterOutNonWhitelistedEvents(events) {
    if (events.length === 0) {
      return [];
    }
    if (!this.clientData.whitelistedEvents) {
      return events;
    }
    return events.filter(
      (event) => this.clientData.whitelistedEvents?.names.includes(event.event_name) || this.clientData.whitelistedEvents?.types.includes(event.event_type)
    );
  }
  splitImpressionEvents(events) {
    const impressions = [];
    const nonImpressions = [];
    for (const event of events) {
      if (event.event_subtype.includes("impression")) {
        impressions.push(event);
      } else {
        nonImpressions.push(event);
      }
    }
    return [impressions, nonImpressions];
  }
  buildQueuedEvent({
    event,
    queuedAt
  }) {
    const event_uuid = uuidv4();
    const updatedEvent = {
      ...typeof event === "function" ? event({ clientData: this.clientData }) : event,
      event_time: queuedAt,
      event_uuid,
      prior_event_uuid: this.clientData.priorEventId || event_uuid,
      client_uuid: this.clientData.clientUuid,
      anonymous_uuid: this.clientData.anonymousUuid,
      session_uuid: this.clientData.sessionUuid,
      attribution_uuid: this.clientData.attributionUuid,
      user_id: this.clientData.userId,
      device: {
        device_type: this.clientData.device.deviceType,
        experience: this.clientData.device.experience,
        useragent: this.clientData.device.userAgent
      }
    };
    this.clientData.priorEventId = event_uuid;
    return updatedEvent;
  }
};

// src/event-tracker/index.ts
function getPriorEventId() {
  return EventTracker.getInstance().getPriorEventId();
}
function initEventTracker(config) {
  EventTracker.getInstance().init(config);
}
function trackEvent(event) {
  EventTracker.getInstance().trackEvent(event);
}

// src/events/deal-events.ts
var convertPrice = (price) => {
  if (!price) {
    return null;
  }
  if (price.toLowerCase() === "free") {
    return 0;
  }
  return Number.parseFloat(price.replace("$", ""));
};
var getPageParam = () => {
  const url = new URL(window.location.href);
  const page = url.searchParams.get("page");
  return page ? Number(page) : null;
};
var trackItemImpression = (data, deal) => {
  trackEvent(
    ({ clientData }) => ({
      ...baseEvents.viewItem,
      data: {
        area: {
          view: buildViewArea(
            {
              entity: window.location.href,
              entity_label: document.title
            },
            clientData
          )
        },
        details: [
          {
            items: [
              {
                item_id: deal.uniqueId,
                item_type: determineItemType(deal.uniqueId),
                item_name: deal.dealTitle,
                item_container_id: data.containerId,
                item_container_name: deal.dealTitle,
                item_original_price: convertPrice(deal.listPriceText),
                item_sale_price: convertPrice(deal.finalPriceText),
                item_index: null,
                item_variant: deal.itemVariant,
                item_attributes: {
                  item_is_expired: deal.isExpired
                }
              }
            ]
          }
        ]
      }
    })
  );
};
var trackItemListImpression = (data, deals) => {
  trackEvent(
    ({ clientData }) => ({
      ...baseEvents.viewItemList,
      data: {
        area: {
          view: buildViewArea(
            {
              entity: window.location.href,
              entity_label: document.title,
              page: getPageParam()
            },
            clientData
          )
        },
        details: [
          {
            items_context: {
              layout_region_id: "",
              layout_region_name: data.regionName,
              layout_region_type: data.regionType,
              layout_container_id: "",
              layout_container_name: data.containerName,
              layout_container_type: "deals"
            },
            items: deals.map((deal, index) => ({
              item_id: deal.uniqueId,
              item_type: determineItemType(deal.uniqueId),
              item_name: deal.dealTitle,
              item_container_id: data.containerId.toString(),
              item_container_name: data.containerName,
              item_original_price: convertPrice(deal.listPriceText),
              item_sale_price: convertPrice(deal.finalPriceText),
              item_index: deal.itemIndex ?? index + 1,
              item_variant: deal.itemVariant,
              item_attributes: {
                item_is_expired: deal.isExpired
              }
            }))
          }
        ]
      }
    })
  );
};
var trackItemListImpressionViewed = (data, deals) => {
  trackEvent(
    ({ clientData }) => ({
      ...baseEvents.viewItemListViewed,
      data: {
        area: {
          view: buildViewArea(
            {
              entity: window.location.href,
              entity_label: document.title,
              page: null
            },
            clientData
          )
        },
        details: [
          {
            items_context: {
              layout_region_id: "",
              layout_region_name: data.regionName,
              layout_region_type: data.regionType,
              layout_container_id: "",
              layout_container_name: data.containerName,
              layout_container_type: "deals"
            },
            items: deals.map((deal, index) => ({
              item_id: deal.uniqueId,
              item_type: determineItemType(deal.uniqueId),
              item_name: deal.dealTitle,
              item_container_id: data.containerId.toString(),
              item_container_name: data.containerName,
              item_original_price: convertPrice(deal.listPriceText),
              item_sale_price: convertPrice(deal.finalPriceText),
              item_index: deal.itemIndex ?? index + 1,
              item_variant: deal.itemVariant,
              item_attributes: {
                item_is_expired: deal.isExpired
              }
            }))
          }
        ]
      }
    })
  );
};
var trackInclick = (data, deal) => {
  trackEvent(
    ({ clientData }) => ({
      ...baseEvents.inclick,
      data: {
        area: {
          view: buildViewArea(
            {
              entity: window.location.href,
              entity_label: document.title,
              page: getPageParam()
            },
            clientData
          ),
          action: {
            action_type: getClickActionType(clientData),
            navigation: {
              destination: data.url,
              navigation_direction: "incoming"
            }
          },
          details: [
            {
              items_context: {
                layout_region_id: data.regionId ?? "",
                layout_region_name: data.regionName,
                layout_region_type: data.regionType,
                layout_container_id: data.containerId ?? "",
                layout_container_name: data.containerName,
                layout_container_type: "deals"
              },
              items: [
                {
                  item_id: deal.uniqueId,
                  item_type: determineItemType(deal.uniqueId),
                  item_name: deal.dealTitle,
                  item_index: (data.dealIndex ?? 0) + 1,
                  item_attributes: {
                    item_is_expired: deal.isExpired
                  }
                }
              ]
            }
          ]
        }
      }
    })
  );
};
var voteTypeToValue = {
  up: "thumbsUp",
  down: "thumbsDown",
  neutral: "neutral"
};
var trackVote = (uniqueId, voteType) => {
  trackEvent(
    ({ clientData }) => ({
      ...baseEvents.vote,
      data: {
        area: {
          view: buildViewArea(
            {
              entity: window.location.href,
              entity_label: document.title
            },
            clientData
          ),
          action: {
            action_type: getClickActionType(clientData),
            button: voteTypeToValue[voteType]
          },
          details: [
            {
              items: [
                {
                  item_id: uniqueId,
                  item_type: determineItemType(uniqueId),
                  item_index: null
                }
              ]
            }
          ]
        }
      }
    })
  );
};
var trackPersonalizationSignal = (uniqueId, signal, feedback) => {
  trackEvent(
    ({ clientData }) => ({
      ...baseEvents.personalizationSignal,
      custom: feedback,
      data: {
        area: {
          view: buildViewArea(
            {
              entity: window.location.href,
              entity_label: document.title
            },
            clientData
          ),
          action: {
            action_type: getClickActionType(clientData),
            button: signal
          },
          details: [
            {
              items: [
                {
                  item_id: uniqueId,
                  item_type: determineItemType(uniqueId),
                  item_index: null
                }
              ]
            }
          ]
        }
      }
    })
  );
};

// src/events/search-events.ts
var trackSearchImpression = (page, deals) => {
  trackEvent(
    ({ clientData }) => ({
      ...baseEvents.viewItemListSearch,
      data: {
        area: {
          view: buildViewArea(
            {
              entity: window.location.href,
              entity_label: document.title,
              page
            },
            clientData
          )
        },
        details: [
          {
            items_context: {
              layout_region_id: "",
              layout_region_name: "Main",
              layout_region_type: "Search",
              layout_container_id: "",
              layout_container_name: "SearchResults",
              layout_container_type: "deals"
            },
            items: deals.map((deal, index) => ({
              item_id: deal.uniqueId,
              item_type: determineItemType(deal.uniqueId),
              item_name: deal.dealTitle,
              item_container_id: "",
              item_container_name: "Main",
              item_index: deal.itemIndex ?? index + 1,
              item_attributes: {
                item_is_expired: deal.isExpired
              }
            }))
          }
        ]
      }
    })
  );
};
var trackSearchImpressionViewed = (page, deals) => {
  trackEvent(
    ({ clientData }) => ({
      ...baseEvents.viewItemListSearchViewed,
      data: {
        area: {
          view: buildViewArea(
            {
              entity: window.location.href,
              entity_label: document.title,
              page
            },
            clientData
          )
        },
        details: [
          {
            items_context: {
              layout_region_id: "",
              layout_region_name: "Main",
              layout_region_type: "Search",
              layout_container_id: "",
              layout_container_name: "SearchResults",
              layout_container_type: "deals"
            },
            items: deals.map((deal, index) => ({
              item_id: deal.uniqueId,
              item_type: determineItemType(deal.uniqueId),
              item_name: deal.dealTitle,
              item_container_id: "",
              item_container_name: "Main",
              item_index: deal.itemIndex ?? index + 1,
              item_attributes: {
                item_is_expired: deal.isExpired
              }
            }))
          }
        ]
      }
    })
  );
};
var mapFilter = (filterSection) => {
  const array = filterSection?.options?.filter((option) => option.checked).map((option) => ({ value: option.value }));
  return array?.length ? array : void 0;
};
var mapPrices = (filterSection) => {
  if (!filterSection) return void 0;
  const { min, max } = filterSection;
  const value = mapFilter(filterSection);
  return [
    {
      value: value?.[0] ? value[0].value : "",
      custom_range: { min: min ?? 0, max: max ?? -1 }
    }
  ];
};
var mapInclusionExclusionFilter = (filterSection) => {
  if (!filterSection?.options) return void 0;
  const include = filterSection.options.filter((option) => option.checked || option.type === "included").map((option) => ({ value: option.value }));
  const exclude = filterSection.options.filter((option) => option.type === "excluded").map((option) => ({ value: option.value }));
  if (include.length === 0 && exclude.length === 0) return void 0;
  if (include.length === 0) include.push({ value: "" });
  return {
    include,
    ...exclude.length > 0 && { exclude }
  };
};
var flattenCategories = (options) => {
  if (!options?.length) return void 0;
  const result = [];
  const recursiveFlatten = (items) => {
    for (const item of items) {
      const { children, ...rest } = item;
      result.push(rest);
      if (children) {
        recursiveFlatten(children);
      }
    }
  };
  recursiveFlatten(options);
  return result;
};
var mapFilters = (filterSections) => {
  if (!filterSections) return void 0;
  const result = {};
  for (const filterSection of filterSections) {
    switch (filterSection.filterLabel) {
      case "display": {
        result.search_display_options = mapFilter(filterSection);
        break;
      }
      case "hideexpired": {
        result.search_display_options = mapFilter(filterSection);
        break;
      }
      case "date": {
        result.search_dates = mapFilter(filterSection);
        break;
      }
      case "previousdays": {
        result.search_dates = mapFilter(filterSection);
        break;
      }
      case "price_range": {
        result.search_prices = mapPrices(filterSection);
        break;
      }
      case "price": {
        result.search_prices = mapPrices(filterSection);
        break;
      }
      case "category": {
        filterSection.options = flattenCategories(filterSection.options);
        result.search_categories = mapInclusionExclusionFilter(filterSection);
        break;
      }
      case "store": {
        result.search_stores = mapInclusionExclusionFilter(filterSection);
        break;
      }
      case "brand": {
        result.search_brands = mapInclusionExclusionFilter(filterSection);
        break;
      }
      case "rating": {
        result.search_ratings = mapFilter(filterSection);
        break;
      }
      case "forum_id": {
        result.search_forums = mapInclusionExclusionFilter(filterSection);
        break;
      }
      case "discount": {
        result.search_discounts = mapInclusionExclusionFilter(filterSection);
        break;
      }
    }
  }
  return result;
};
var createSearchActionEvent = (eventType, data, filterSections, clientData) => ({
  ...eventType,
  data: {
    area: {
      view: buildViewArea(
        {
          entity: window.location.href,
          entity_label: document.title
        },
        clientData
      ),
      action: {
        action_type: getClickActionType(clientData),
        button: "search"
      },
      details: {
        search_criteria: {
          search_options: {
            search_term: data.searchTerm
          },
          search_sort_by: data.searchSortBy,
          total_results: data.totalResults
        },
        search_parameters: mapFilters(filterSections)
      }
    }
  }
});
var trackSearchAction = (data, filterSections, event = baseEvents.search) => trackEvent(
  ({ clientData }) => createSearchActionEvent(event, data, filterSections, clientData)
);
var trackSearchRefinementAction = (data, filterSections) => trackSearchAction(data, filterSections, baseEvents.searchRefinement);

// src/events/interactions.ts
var trackOptInAction = () => {
  trackEvent(
    ({ clientData }) => ({
      ...baseEvents.optInAction,
      data: {
        area: {
          view: buildViewArea(
            {
              entity: window.location.href,
              entity_label: document.title
            },
            clientData
          ),
          action: {
            action_type: getClickActionType(clientData),
            button: "opt-in"
          }
        }
      }
    })
  );
};
var trackOptOutAction = (button = "opt-out") => {
  trackEvent(
    ({ clientData }) => ({
      ...baseEvents.optOutAction,
      data: {
        area: {
          view: buildViewArea(
            {
              entity: window.location.href,
              entity_label: document.title
            },
            clientData
          ),
          action: {
            action_type: getClickActionType(clientData),
            button
          }
        }
      }
    })
  );
};
export {
  EventHeaders,
  EventQueryParameters,
  buildViewArea,
  determineItemType,
  getClickActionType,
  getPriorEventId,
  initEventTracker,
  parseEventDataFromDataLayer,
  parseEventDataFromUrl,
  trackEvent,
  trackInclick,
  trackItemImpression,
  trackItemListImpression,
  trackItemListImpressionViewed,
  trackOptInAction,
  trackOptOutAction,
  trackPersonalizationSignal,
  trackSearchAction,
  trackSearchImpression,
  trackSearchImpressionViewed,
  trackSearchRefinementAction,
  trackVote
};
